import React, { useState, useEffect } from "react";
import { 
  Users, CheckSquare, Plus, Activity, Calendar, Award, BookOpen, 
  Sparkles, FileText, Check, MessageCircle, DollarSign, Clock, ListCollapse
} from "lucide-react";
import axios from "axios";

export default function Dashboard({ user, onOpenAuth }) {
  // Common states
  const [activeTab, setActiveTab] = useState(user?.role === "admin" ? "overview" : "my-programs");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Standard user states
  const [myPrograms, setMyPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [expandedDay, setExpandedDay] = useState(1);
  const [reflectionText, setReflectionText] = useState("");

  const [mealPlan, setMealPlan] = useState(null);
  const [hasPendingMealReq, setHasPendingMealReq] = useState(false);
  const [pendingMealDetails, setPendingMealDetails] = useState(null);
  const [currentWeight, setCurrentWeight] = useState("");
  const [targetWeight, setTargetWeight] = useState("");
  const [fitnessGoals, setFitnessGoals] = useState("");
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [mealReqNotes, setMealReqNotes] = useState("");

  const [myBookings, setMyBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [bookingNotes, setBookingNotes] = useState("");

  const [reflections, setReflections] = useState([]);
  const [todayDevotional, setTodayDevotional] = useState(null);
  const [dailyReflectionInput, setDailyReflectionInput] = useState("");

  // Admin states
  const [adminOverview, setAdminOverview] = useState(null);
  const [allBookings, setAllBookings] = useState([]);
  const [mealRequests, setMealRequests] = useState([]);
  const [assigningToRequest, setAssigningToRequest] = useState(null); // Request object
  
  // Dynamic Assign Meal form state
  const [targetCalories, setTargetCalories] = useState(2000);
  const [targetProtein, setTargetProtein] = useState(150);
  const [targetCarbs, setTargetCarbs] = useState(200);
  const [targetFats, setTargetFats] = useState(65);
  const [assignedMeals, setAssignedMeals] = useState([
    { meal_type: "Breakfast", name: "Oatmeal with whey & berries", calories: 450, protein: 35, carbs: 50, fats: 8 },
    { meal_type: "Lunch", name: "Grilled chicken, white rice & broccoli", calories: 600, protein: 45, carbs: 65, fats: 10 },
    { meal_type: "Dinner", name: "Pan-seared salmon & sweet potato", calories: 550, protein: 40, carbs: 45, fats: 18 },
    { meal_type: "Snack", name: "Almonds & Greek yogurt", calories: 400, protein: 30, carbs: 40, fats: 29 }
  ]);

  // Generate list of next 7 days for slot-picker booking
  const getUpcomingDays = () => {
    const result = [];
    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    for (let i = 1; i <= 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      d.setHours(0, 0, 0, 0);
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const dy = String(d.getDate()).padStart(2, "0");
      result.push({
        id: `${yr}-${mo}-${dy}`,
        dateString: `${yr}-${mo}-${dy}`,
        label: `${daysOfWeek[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}`,
        fullDate: new Date(d)
      });
    }
    return result;
  };
  const [dates_to_add] = useState(getUpcomingDays());

  // Initial loads
  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        fetchAdminOverview();
        fetchAllBookings();
        fetchMealRequests();
      } else {
        fetchMyPrograms();
        fetchMealPlan();
        fetchMyBookings();
        fetchMyReflections();
        fetchTodayDevotional();
        fetchAvailableSlots();
      }
    }
  }, [user, activeTab]);

  // Standard User APIs
  const fetchMyPrograms = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/programs/my`, { withCredentials: true });
      setMyPrograms(data);
      if (data.length > 0 && !selectedProgram) {
        setSelectedProgram(data[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMealPlan = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/meals/my`, { withCredentials: true });
      setMealPlan(data.plan);
      setHasPendingMealReq(data.has_pending_request);
      setPendingMealDetails(data.pending_request_details);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMyBookings = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/coaching/my`, { withCredentials: true });
      setMyBookings(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/coaching/slots`);
      setSlots(data);
      if (data.length > 0) {
        setSelectedTime(data[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMyReflections = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/spiritual/my-reflections`, { withCredentials: true });
      setReflections(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTodayDevotional = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/spiritual/today`);
      setTodayDevotional(data);
    } catch (err) {
      console.error(err);
    }
  };

  // Submit coaching session booking
  const handleBookSession = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    if (!selectedDate) {
      setError("Please select a preferred date for your virtual session.");
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${API_URL}/api/coaching/book`,
        {
          client_name: user.name,
          email: user.email,
          preferred_date: selectedDate,
          preferred_time: selectedTime,
          notes: bookingNotes
        },
        { withCredentials: true }
      );
      setSuccess("Your virtual 1-on-1 session inquiry was successfully sent to Stokes! Approve state can be checked on your dashboard shortly.");
      setBookingNotes("");
      setSelectedDate("");
      fetchMyBookings();
    } catch (err) {
      setError(err.response?.data?.detail || "Booking submission failed.");
    } finally {
      setLoading(false);
    }
  };

  // Submit custom meal request
  const handleRequestMealPlan = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${API_URL}/api/meals/request`,
        {
          current_weight: currentWeight,
          target_weight: targetWeight,
          fitness_goals: fitnessGoals,
          dietary_restrictions: dietaryRestrictions,
          additional_notes: mealReqNotes
        },
        { withCredentials: true }
      );
      setSuccess("Custom Meal Plan requested! Instructor Stokes will review and build a customized layout for you.");
      setCurrentWeight("");
      setTargetWeight("");
      setFitnessGoals("");
      setDietaryRestrictions("");
      setMealReqNotes("");
      fetchMealPlan();
    } catch (err) {
      setError(err.response?.data?.detail || "Meal plan request failed.");
    } finally {
      setLoading(false);
    }
  };

  // Log a daily meal as eaten
  const handleToggleMeal = async (mealName, currentlyEaten) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(
        `${API_URL}/api/meals/my/log`,
        {
          meal_id: mealName,
          eaten: !currentlyEaten
        },
        { withCredentials: true }
      );

      setMealPlan((prev) => ({
        ...prev,
        logged_meals: data.logged_meals
      }));
    } catch (err) {
      console.error(err);
    }
  };

  // Log workout day completed
  const handleCompleteWorkout = async (dayIndex) => {
    if (!selectedProgram) return;
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(
        `${API_URL}/api/programs/my/${selectedProgram.id}/workout/${dayIndex}/complete`,
        { reflection: reflectionText },
        { withCredentials: true }
      );

      setSuccess(`Day ${dayIndex} marked as completed. Praise God for the strength!`);
      setReflectionText("");
      
      // Update local state to show checkbox as checked
      setMyPrograms((prevProgs) =>
        prevProgs.map((p) => {
          if (p.id === selectedProgram.id) {
            return {
              ...p,
              completed_days: data.completed_days
            };
          }
          return p;
        })
      );
      
      // Update selected program reference
      setSelectedProgram((prev) => ({
        ...prev,
        completed_days: data.completed_days
      }));

    } catch (err) {
      setError(err.response?.data?.detail || "Completion log failed.");
    } finally {
      setLoading(false);
    }
  };

  // Complete standalone Daily Devotional reflection log
  const handleLogDailyDevotional = async (e) => {
    e.preventDefault();
    if (!dailyReflectionInput.trim()) return;
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${API_URL}/api/spiritual/devotional/complete`,
        { reflections: dailyReflectionInput },
        { withCredentials: true }
      );

      setSuccess("Your devotional reflection has been logged in your Spiritual Journal!");
      setDailyReflectionInput("");
      fetchMyReflections();
    } catch (err) {
      setError(err.response?.data?.detail || "Journal log failed.");
    } finally {
      setLoading(false);
    }
  };


  // ADMIN APIs
  const fetchAdminOverview = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/admin/overview`, { withCredentials: true });
      setAdminOverview(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAllBookings = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/coaching/all`, { withCredentials: true });
      setAllBookings(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMealRequests = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/meals/requests`, { withCredentials: true });
      setMealRequests(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleApproveBooking = async (id) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/coaching/${id}/approve`, {}, { withCredentials: true });
      setSuccess("Booking inquiry successfully approved.");
      fetchAllBookings();
      fetchAdminOverview();
    } catch (err) {
      setError("Approval action failed.");
    }
  };

  const handleDeclineBooking = async (id) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/coaching/${id}/decline`, {}, { withCredentials: true });
      setSuccess("Booking inquiry declined.");
      fetchAllBookings();
      fetchAdminOverview();
    } catch (err) {
      setError("Decline action failed.");
    }
  };

  // Open dynamic meal assigning panel for a specific user request
  const handleOpenAssignMeals = (req) => {
    setAssigningToRequest(req);
    // Auto populate targets
    setTargetCalories(2000);
    setTargetProtein(150);
    setTargetCarbs(200);
    setTargetFats(65);
  };

  const handleUpdateMealRow = (index, field, val) => {
    const updated = [...assignedMeals];
    updated[index][field] = field === "name" || field === "meal_type" ? val : parseInt(val) || 0;
    setAssignedMeals(updated);
  };

  const handleAddMealRow = () => {
    setAssignedMeals([
      ...assignedMeals,
      { meal_type: "Snack", name: "New Customized Item", calories: 200, protein: 15, carbs: 20, fats: 5 }
    ]);
  };

  const handleRemoveMealRow = (index) => {
    setAssignedMeals(assignedMeals.filter((_, i) => i !== index));
  };

  const handleSubmitMealAssignment = async (e) => {
    e.preventDefault();
    if (!assigningToRequest) return;
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${API_URL}/api/meals/${assigningToRequest.id}/assign`,
        {
          target_calories: targetCalories,
          target_protein: targetProtein,
          target_carbs: targetCarbs,
          target_fats: targetFats,
          meals: assignedMeals
        },
        { withCredentials: true }
      );

      setSuccess(`Custom meal plan successfully assigned to ${assigningToRequest.user_name}!`);
      setAssigningToRequest(null);
      fetchMealRequests();
      fetchAdminOverview();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to assign meal plan.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="max-w-7xl mx-auto px-6 py-12 font-manrope text-white min-h-[80vh]">
      {/* Messages */}
      {error && (
        <div 
          className="mb-6 bg-white text-black px-6 py-4 text-xs uppercase tracking-widest font-extrabold"
          data-testid="dashboard-error-alert"
        >
          {error}
        </div>
      )}
      {success && (
        <div 
          className="mb-6 border border-white text-white px-6 py-4 text-xs uppercase tracking-widest font-extrabold"
          data-testid="dashboard-success-alert"
        >
          {success}
        </div>
      )}

      {/* RENDER ADMIN DASHBOARD */}
      {user?.role === "admin" ? (
        <div className="space-y-10" data-testid="admin-dashboard-container">
          <div className="border-b border-neutral-800 pb-6">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
              SSA INSTRUCTOR COMMAND CENTER
            </span>
            <h1 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">
              Instructor Dashboard • ForgedByStokes
            </h1>
          </div>

          {/* Admin Navigation Tab Links */}
          <div className="flex border-b border-neutral-800 text-xs uppercase tracking-widest font-semibold gap-8">
            <button
              onClick={() => { setActiveTab("overview"); setError(""); setSuccess(""); }}
              className={`pb-3 border-b-2 transition-all focus:outline-none ${
                activeTab === "overview" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="admin-tab-overview"
            >
              Overview Stats
            </button>
            <button
              onClick={() => { setActiveTab("coaching-approvals"); setError(""); setSuccess(""); }}
              className={`pb-3 border-b-2 transition-all focus:outline-none ${
                activeTab === "coaching-approvals" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="admin-tab-bookings"
            >
              Coaching Bookings ({allBookings.filter(b => b.status === "pending").length})
            </button>
            <button
              onClick={() => { setActiveTab("meal-assignments"); setError(""); setSuccess(""); }}
              className={`pb-3 border-b-2 transition-all focus:outline-none ${
                activeTab === "meal-assignments" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="admin-tab-meals"
            >
              Meal Requests ({mealRequests.length})
            </button>
          </div>

          {/* TAB 1: OVERVIEW */}
          {activeTab === "overview" && adminOverview && (
            <div className="space-y-8" data-testid="admin-overview-view">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="border border-neutral-800 p-6 bg-neutral-950/20">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Total Users</span>
                    <Users className="w-4 h-4 text-neutral-500" />
                  </div>
                  <h3 className="font-bebas text-4xl mt-3" data-testid="stat-users">{adminOverview.users_count}</h3>
                </div>

                <div className="border border-neutral-800 p-6 bg-neutral-950/20">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Simulated Revenue</span>
                    <DollarSign className="w-4 h-4 text-neutral-500" />
                  </div>
                  <h3 className="font-bebas text-4xl mt-3" data-testid="stat-revenue">${adminOverview.total_revenue}</h3>
                </div>

                <div className="border border-neutral-800 p-6 bg-neutral-950/20">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Pending Bookings</span>
                    <Calendar className="w-4 h-4 text-neutral-500" />
                  </div>
                  <h3 className="font-bebas text-4xl mt-3" data-testid="stat-bookings">{adminOverview.pending_bookings}</h3>
                </div>

                <div className="border border-neutral-800 p-6 bg-neutral-950/20">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Meal Demands</span>
                    <Activity className="w-4 h-4 text-neutral-500" />
                  </div>
                  <h3 className="font-bebas text-4xl mt-3" data-testid="stat-meals">{adminOverview.pending_meal_plans}</h3>
                </div>
              </div>

              {/* Recent Sales Transaction Table */}
              <div className="border border-neutral-800 bg-neutral-950/20 p-8">
                <h3 className="font-bebas text-xl tracking-wide uppercase mb-4">Simulated Transactions Log</h3>
                {adminOverview.recent_sales && adminOverview.recent_sales.length === 0 ? (
                  <p className="text-sm text-neutral-500">No checkout simulation orders processed yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs uppercase tracking-wider font-mono">
                      <thead>
                        <tr className="border-b border-neutral-800 text-neutral-400">
                          <th className="py-3">Client Identifier</th>
                          <th className="py-3">Item Purchased</th>
                          <th className="py-3">Price</th>
                          <th className="py-3 text-right">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-900">
                        {adminOverview.recent_sales?.map((sale, i) => (
                          <tr key={i} className="hover:bg-neutral-900/30">
                            <td className="py-3">{sale.user_name}</td>
                            <td className="py-3 text-white font-bold">{sale.item_name}</td>
                            <td className="py-3 font-bold">${sale.price}</td>
                            <td className="py-3 text-right text-neutral-500">
                              {sale.date ? new Date(sale.date).toLocaleDateString() : "Just Now"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: COACHING BOOKINGS APPROVALS */}
          {activeTab === "coaching-approvals" && (
            <div className="space-y-6" data-testid="admin-bookings-view">
              <h3 className="font-bebas text-2xl uppercase tracking-wide">1-on-1 Virtual Session Inquiries</h3>
              {allBookings.length === 0 ? (
                <div className="border border-dashed border-neutral-800 py-12 text-center text-sm text-neutral-500 uppercase tracking-widest">
                  No virtual session bookings received.
                </div>
              ) : (
                <div className="space-y-4">
                  {allBookings.map((b) => (
                    <div 
                      key={b.id} 
                      className="border border-neutral-800 p-6 bg-neutral-950/20 flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                      data-testid={`admin-booking-item-${b.id}`}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 ${
                            b.status === "approved" ? "bg-white text-black" : b.status === "declined" ? "bg-neutral-800 text-neutral-400" : "border border-white text-white"
                          }`}>
                            {b.status}
                          </span>
                          <span className="font-bebas text-lg uppercase tracking-wide">{b.client_name}</span>
                          <span className="text-neutral-500 font-mono text-[10px]">({b.email})</span>
                        </div>
                        <p className="text-xs text-neutral-400 uppercase tracking-wider font-semibold flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>Preferred: {b.preferred_date} @ {b.preferred_time}</span>
                        </p>
                        {b.notes && (
                          <p className="text-xs text-neutral-300 font-manrope font-light bg-black/40 p-3 border-l border-neutral-700">
                            " {b.notes} "
                          </p>
                        )}
                      </div>

                      {b.status === "pending" && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApproveBooking(b.id)}
                            className="bg-white text-black px-4 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-neutral-200 transition-all focus:outline-none"
                            data-testid={`approve-button-${b.id}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleDeclineBooking(b.id)}
                            className="border border-neutral-800 hover:border-neutral-500 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white transition-all focus:outline-none"
                            data-testid={`decline-button-${b.id}`}
                          >
                            Decline
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: CUSTOM MEAL ASSIGNMENTS */}
          {activeTab === "meal-assignments" && (
            <div className="space-y-6" data-testid="admin-meals-view">
              <h3 className="font-bebas text-2xl uppercase tracking-wide">Meal Plan Demands From Clients</h3>
              {mealRequests.length === 0 ? (
                <div className="border border-dashed border-neutral-800 py-12 text-center text-sm text-neutral-500 uppercase tracking-widest">
                  No customized meal plan requests pending.
                </div>
              ) : (
                <div className="space-y-4">
                  {mealRequests.map((req) => (
                    <div 
                      key={req.id} 
                      className="border border-neutral-800 p-6 bg-neutral-950/20"
                      data-testid={`admin-meal-req-item-${req.id}`}
                    >
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 pb-4 border-b border-neutral-900">
                        <div>
                          <h4 className="font-bebas text-lg tracking-wide uppercase">{req.user_name}</h4>
                          <span className="text-[10px] font-mono text-neutral-500">Email: {req.user_email} • Current: {req.current_weight}lbs • Target: {req.target_weight}lbs</span>
                        </div>
                        
                        {!assigningToRequest || assigningToRequest.id !== req.id ? (
                          <button
                            onClick={() => handleOpenAssignMeals(req)}
                            className="bg-white text-black px-4 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-neutral-200 transition-all focus:outline-none flex items-center gap-1.5"
                            data-testid={`assign-plan-btn-${req.id}`}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Build & Assign Customized Layout</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => setAssigningToRequest(null)}
                            className="border border-neutral-800 hover:border-neutral-500 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white transition-all focus:outline-none flex items-center gap-1.5"
                          >
                            <ListCollapse className="w-3.5 h-3.5" />
                            <span>Cancel Assignment</span>
                          </button>
                        )}
                      </div>

                      {/* Request Specifications */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-manrope">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-neutral-500 block mb-1">FITNESS GOALS</span>
                          <p className="text-neutral-300 leading-relaxed">{req.fitness_goals}</p>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-neutral-500 block mb-1">DIETARY RESTRICTIONS</span>
                          <p className="text-neutral-300 leading-relaxed">{req.dietary_restrictions || "None specified"}</p>
                        </div>
                      </div>

                      {/* Expandable meal assigning form */}
                      {assigningToRequest && assigningToRequest.id === req.id && (
                        <form onSubmit={handleSubmitMealAssignment} className="mt-8 border border-neutral-800 p-6 bg-black/40 space-y-6" data-testid="assignment-form">
                          <h4 className="font-bebas text-xl uppercase tracking-wider">Custom Layout Architect</h4>
                          
                          {/* Target Macros inputs */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                            <div className="space-y-1">
                              <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Calories (kcal)</label>
                              <input
                                type="number"
                                required
                                value={targetCalories}
                                onChange={(e) => setTargetCalories(parseInt(e.target.value) || 0)}
                                className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-white rounded-none focus:border-white focus:outline-none"
                                data-testid="assign-calories"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Protein (g)</label>
                              <input
                                type="number"
                                required
                                value={targetProtein}
                                onChange={(e) => setTargetProtein(parseInt(e.target.value) || 0)}
                                className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-white rounded-none focus:border-white focus:outline-none"
                                data-testid="assign-protein"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Carbs (g)</label>
                              <input
                                type="number"
                                required
                                value={targetCarbs}
                                onChange={(e) => setTargetCarbs(parseInt(e.target.value) || 0)}
                                className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-white rounded-none focus:border-white focus:outline-none"
                                data-testid="assign-carbs"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Fats (g)</label>
                              <input
                                type="number"
                                required
                                value={targetFats}
                                onChange={(e) => setTargetFats(parseInt(e.target.value) || 0)}
                                className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-white rounded-none focus:border-white focus:outline-none"
                                data-testid="assign-fats"
                              />
                            </div>
                          </div>

                          {/* Dynamic Meal List Editor */}
                          <div className="space-y-3">
                            <div className="flex justify-between items-center border-b border-neutral-900 pb-1.5">
                              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Meal Components</span>
                              <button
                                type="button"
                                onClick={handleAddMealRow}
                                className="border border-white hover:bg-white hover:text-black px-3 py-1 text-[9px] uppercase font-bold tracking-widest"
                                data-testid="add-meal-row-btn"
                              >
                                Add Meal Row
                              </button>
                            </div>

                            <div className="space-y-3">
                              {assignedMeals.map((meal, index) => (
                                <div key={index} className="grid grid-cols-12 gap-3 items-center" data-testid={`meal-row-${index}`}>
                                  {/* Meal Type selection */}
                                  <div className="col-span-3">
                                    <select
                                      value={meal.meal_type}
                                      onChange={(e) => handleUpdateMealRow(index, "meal_type", e.target.value)}
                                      className="w-full bg-neutral-950 border-b border-neutral-800 py-1.5 text-xs text-white uppercase focus:outline-none"
                                      data-testid={`meal-type-${index}`}
                                    >
                                      <option value="Breakfast">Breakfast</option>
                                      <option value="Lunch">Lunch</option>
                                      <option value="Dinner">Dinner</option>
                                      <option value="Snack">Snack</option>
                                    </select>
                                  </div>

                                  {/* Meal Name input */}
                                  <div className="col-span-5">
                                    <input
                                      type="text"
                                      required
                                      value={meal.name}
                                      placeholder="Meal specification"
                                      onChange={(e) => handleUpdateMealRow(index, "name", e.target.value)}
                                      className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white focus:border-white focus:outline-none"
                                      data-testid={`meal-name-${index}`}
                                    />
                                  </div>

                                  {/* Calories input */}
                                  <div className="col-span-2">
                                    <input
                                      type="number"
                                      required
                                      value={meal.calories}
                                      onChange={(e) => handleUpdateMealRow(index, "calories", e.target.value)}
                                      className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white focus:outline-none text-center"
                                      data-testid={`meal-calories-${index}`}
                                      placeholder="kcal"
                                    />
                                  </div>

                                  {/* Delete meal row action */}
                                  <div className="col-span-2 text-right">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveMealRow(index)}
                                      className="text-neutral-500 hover:text-white text-xs uppercase"
                                      data-testid={`remove-meal-row-${index}`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all duration-300"
                            data-testid="assign-meal-submit-btn"
                          >
                            {loading ? "Assigning Layout..." : `Assign Customized Plan to ${req.user_name}`}
                          </button>
                        </form>
                      )}

                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      ) : (
        /* RENDER STANDARD USER DASHBOARD */
        <div className="space-y-10" data-testid="user-dashboard-container">
          <div className="border-b border-neutral-800 pb-6 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold block mb-1">
                MY ATHLETIC & SPIRITUAL TEMPLE
              </span>
              <h1 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">
                Welcome Back, {user?.name}
              </h1>
            </div>
            <span className="text-xs uppercase font-mono tracking-widest text-neutral-400 bg-neutral-900 border border-neutral-800 px-4 py-1.5 font-bold">
              Accountability Standard: Standard Client
            </span>
          </div>

          {/* Standard User Tabs navigation */}
          <div className="flex border-b border-neutral-800 text-xs uppercase tracking-widest font-semibold gap-8 overflow-x-auto pb-1">
            <button
              onClick={() => { setActiveTab("my-programs"); setError(""); setSuccess(""); }}
              className={`pb-3 border-b-2 transition-all focus:outline-none whitespace-nowrap ${
                activeTab === "my-programs" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="user-tab-programs"
            >
              My Purchased Programs ({myPrograms.length})
            </button>
            <button
              onClick={() => { setActiveTab("my-meal-planner"); setError(""); setSuccess(""); }}
              className={`pb-3 border-b-2 transition-all focus:outline-none whitespace-nowrap ${
                activeTab === "my-meal-planner" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="user-tab-meals"
            >
              My Custom Meal Plan
            </button>
            <button
              onClick={() => { setActiveTab("my-coaching"); setError(""); setSuccess(""); }}
              className={`pb-3 border-b-2 transition-all focus:outline-none whitespace-nowrap ${
                activeTab === "my-coaching" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="user-tab-bookings"
            >
              My Coaching Sessions ({myBookings.length})
            </button>
            <button
              onClick={() => { setActiveTab("spiritual-journal"); setError(""); setSuccess(""); }}
              className={`pb-3 border-b-2 transition-all focus:outline-none whitespace-nowrap ${
                activeTab === "spiritual-journal" ? "border-white text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
              data-testid="user-tab-journal"
            >
              Spiritual growth journal
            </button>
          </div>

          {/* TAB 1: MY PURCHASED PROGRAMS */}
          {activeTab === "my-programs" && (
            <div className="space-y-8" data-testid="user-programs-view">
              {myPrograms.length === 0 ? (
                <div className="border border-dashed border-neutral-800 py-16 text-center space-y-4">
                  <Award className="w-8 h-8 text-neutral-700 mx-auto" />
                  <p className="text-sm text-neutral-500 uppercase tracking-widest">
                    You haven't bought any premade programs yet.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                  {/* Left List of Purchased Programs */}
                  <div className="lg:col-span-4 space-y-4">
                    <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block pb-2 border-b border-neutral-900">
                      Purchased Templates
                    </span>
                    {myPrograms.map((prog) => (
                      <button
                        key={prog.id}
                        onClick={() => { setSelectedProgram(prog); setExpandedDay(1); }}
                        className={`w-full text-left p-4 border transition-all relative rounded-none flex justify-between items-center ${
                          selectedProgram?.id === prog.id 
                            ? "bg-neutral-900/50 border-white text-white" 
                            : "border-neutral-800 hover:border-neutral-500 text-neutral-400 hover:text-white"
                        }`}
                        data-testid={`purchased-program-${prog.id}`}
                      >
                        <div>
                          <h4 className="font-bebas text-lg uppercase tracking-wide">{prog.name}</h4>
                          <span className="text-[9px] uppercase tracking-widest font-mono text-neutral-500 block">
                            Duration: {prog.duration}
                          </span>
                        </div>
                        <div className="text-xs uppercase font-mono text-neutral-400 font-bold bg-neutral-950 px-2 py-1">
                          {prog.completed_days?.length || 0} Complete
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Right Program detail & Workout Tracker checklist */}
                  {selectedProgram && (
                    <div className="lg:col-span-8 border border-neutral-800 p-8 bg-neutral-950/20" data-testid="program-detail-panel">
                      <div className="flex justify-between items-start gap-4 mb-6 pb-6 border-b border-neutral-900">
                        <div>
                          <span className="text-[9px] uppercase tracking-widest bg-white text-black px-1.5 py-0.5 font-bold">
                            {selectedProgram.category}
                          </span>
                          <h2 className="font-bebas text-3xl uppercase tracking-wide mt-2">{selectedProgram.name}</h2>
                          <p className="text-xs text-neutral-400 mt-1 max-w-xl font-manrope">{selectedProgram.description}</p>
                        </div>
                        <div className="text-right font-mono text-xs text-neutral-500 font-bold">
                          Progress Tracker: {selectedProgram.completed_days?.length || 0} completed days
                        </div>
                      </div>

                      {/* Downloadable PDF program */}
                      {selectedProgram.pdf_url && (
                        <a
                          href={selectedProgram.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="flex items-center justify-center gap-2 w-full bg-white text-black py-3 mb-6 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all"
                          data-testid="download-program-pdf"
                        >
                          <FileText className="w-4 h-4" />
                          <span>Download Program PDF</span>
                        </a>
                      )}

                      {/* Checklist layout */}
                      <div className="space-y-6">
                        {selectedProgram.workouts?.length > 0 ? (
                        <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block mb-4">
                          Training Matrix & Devotions ({selectedProgram.workouts?.length || 0} Days Available)
                        </span>
                        ) : (
                        <p className="text-xs text-neutral-500 leading-relaxed">
                          This is a downloadable program. Use the button above to access your complete PDF guide and train at your own pace.
                        </p>
                        )}

                        <div className="space-y-4">
                          {selectedProgram.workouts?.map((workout) => {
                            const isCompleted = selectedProgram.completed_days?.includes(workout.day);
                            const isExpanded = expandedDay === workout.day;

                            return (
                              <div 
                                key={workout.day}
                                className={`border transition-all ${
                                  isExpanded ? "border-neutral-500 bg-neutral-950/40" : "border-neutral-800 hover:border-neutral-700 bg-neutral-950/10"
                                }`}
                                data-testid={`workout-day-card-${workout.day}`}
                              >
                                {/* Header Toggle button */}
                                <button
                                  onClick={() => setExpandedDay(isExpanded ? null : workout.day)}
                                  className="w-full flex items-center justify-between p-4 focus:outline-none"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-6 h-6 border flex items-center justify-center text-xs font-bold font-mono ${
                                      isCompleted ? "bg-white text-black border-white" : "border-neutral-800 text-neutral-400"
                                    }`}>
                                      {isCompleted ? <Check className="w-3.5 h-3.5" /> : workout.day}
                                    </div>
                                    <div className="text-left">
                                      <h5 className={`font-bebas text-lg tracking-wide uppercase ${isCompleted ? "line-through text-neutral-500" : ""}`}>
                                        Day {workout.day}: {workout.title}
                                      </h5>
                                    </div>
                                  </div>
                                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold font-mono">
                                    {isCompleted ? "COMPLETED" : "CLICK TO VIEW"}
                                  </span>
                                </button>

                                {/* Detailed view expanded */}
                                {isExpanded && (
                                  <div className="p-6 border-t border-neutral-900 bg-neutral-950/60 space-y-6">
                                    {/* Scripture Devotional bento card */}
                                    <div className="border border-neutral-800 p-5 bg-black/60 relative">
                                      <div className="absolute top-0 right-0 border-b border-l border-neutral-800 px-3 py-1 font-bebas text-[9px] text-neutral-500">
                                        SACRED STEWARDSHIP
                                      </div>
                                      <span className="text-[9px] uppercase font-bold tracking-widest text-neutral-400 block mb-1">
                                        Today's Scripture: {workout.scripture}
                                      </span>
                                      <blockquote className="italic text-sm text-neutral-200 border-l-2 border-white pl-4 my-3 font-light font-manrope">
                                        " {workout.devotional} "
                                      </blockquote>
                                    </div>

                                    {/* Exercise checklists */}
                                    <div className="space-y-3">
                                      <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block mb-2 font-mono">
                                        PRESCRIPTION DRILLS
                                      </span>
                                      <div className="divide-y divide-neutral-900">
                                        {workout.exercises?.map((ex, exIdx) => (
                                          <div key={exIdx} className="py-2.5 flex justify-between items-center text-xs">
                                            <div className="flex items-center gap-3">
                                              <span className="font-bold text-white uppercase font-manrope">{ex.name}</span>
                                            </div>
                                            <div className="font-mono text-neutral-400">
                                              {ex.sets} Sets x {ex.reps} {ex.weight ? `• ${ex.weight}` : ""}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Interactive completion action form */}
                                    {!isCompleted ? (
                                      <div className="pt-4 border-t border-neutral-900 space-y-3">
                                        <div className="space-y-1">
                                          <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                                            Daily reflection / Journal note
                                          </label>
                                          <textarea
                                            placeholder="Write your physical notes or a brief prayer reflecting on today's scripture..."
                                            value={reflectionText}
                                            onChange={(e) => setReflectionText(e.target.value)}
                                            rows={2}
                                            className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none resize-none"
                                            data-testid={`workout-reflection-${workout.day}`}
                                          />
                                        </div>

                                        <button
                                          onClick={() => handleCompleteWorkout(workout.day)}
                                          disabled={loading}
                                          className="w-full bg-white text-black py-2 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all flex items-center justify-center gap-1.5"
                                          data-testid={`complete-workout-btn-${workout.day}`}
                                        >
                                          <Check className="w-4 h-4" />
                                          <span>{loading ? "Recording..." : "Log Completed Workout"}</span>
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="pt-4 border-t border-neutral-900 flex justify-between items-center text-xs">
                                        <span className="text-neutral-500 uppercase font-mono">Status</span>
                                        <div className="flex items-center gap-1.5 text-neutral-400 bg-neutral-900 border border-neutral-800 px-3 py-1 font-bold">
                                          <Check className="w-3.5 h-3.5 text-white" />
                                          <span>Completed & Logged</span>
                                        </div>
                                      </div>
                                    )}

                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: MY CUSTOM MEAL PLANNER */}
          {activeTab === "my-meal-planner" && (
            <div className="space-y-8" data-testid="user-meals-view">
              {mealPlan ? (
                /* Assigned Meal Plan layout details */
                <div className="space-y-8" data-testid="assigned-meal-plan-panel">
                  {/* Macros Bento Banner */}
                  <div className="border border-neutral-800 p-8 bg-neutral-950/20 relative">
                    <div className="absolute top-0 right-0 border-b border-l border-neutral-800 px-3 py-1 font-bebas text-[9px] text-neutral-500">
                      SACRED STEWARDSHIP
                    </div>
                    <span className="text-[10px] uppercase font-bold text-neutral-500 block mb-1">
                      ASSIGNED CUSTOM MACRONUTRIENT TARGETS
                    </span>
                    <h2 className="font-bebas text-3xl uppercase tracking-wide mb-6">Nutrition Architect</h2>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center font-mono">
                      <div className="border border-neutral-800 py-4 bg-black/40">
                        <span className="text-[9px] uppercase tracking-widest text-neutral-500 block font-bold mb-1">Calories</span>
                        <span className="font-bebas text-2xl text-white font-extrabold" data-testid="macro-calories">{mealPlan.target_calories} kcal</span>
                      </div>
                      <div className="border border-neutral-800 py-4 bg-black/40">
                        <span className="text-[9px] uppercase tracking-widest text-neutral-500 block font-bold mb-1">Protein</span>
                        <span className="font-bebas text-2xl text-white font-extrabold" data-testid="macro-protein">{mealPlan.target_protein}g</span>
                      </div>
                      <div className="border border-neutral-800 py-4 bg-black/40">
                        <span className="text-[9px] uppercase tracking-widest text-neutral-500 block font-bold mb-1">Carbohydrates</span>
                        <span className="font-bebas text-2xl text-white font-extrabold" data-testid="macro-carbs">{mealPlan.target_carbs}g</span>
                      </div>
                      <div className="border border-neutral-800 py-4 bg-black/40">
                        <span className="text-[9px] uppercase tracking-widest text-neutral-500 block font-bold mb-1">Fats</span>
                        <span className="font-bebas text-2xl text-white font-extrabold" data-testid="macro-fats">{mealPlan.target_fats}g</span>
                      </div>
                    </div>
                  </div>

                  {/* Daily Meal log Checklist */}
                  <div className="border border-neutral-800 p-8 bg-neutral-950/20">
                    <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block mb-4">
                      Today's Nutrient Logs ({mealPlan.meals?.length || 0} meals)
                    </span>

                    <div className="divide-y divide-neutral-900">
                      {mealPlan.meals?.map((meal, idx) => {
                        const isEaten = mealPlan.logged_meals?.includes(meal.name);
                        return (
                          <div 
                            key={idx} 
                            className="py-4 flex justify-between items-center"
                            data-testid={`meal-item-${idx}`}
                          >
                            <div className="flex items-center gap-4">
                              {/* Toggle Checkbox */}
                              <button
                                onClick={() => handleToggleMeal(meal.name, isEaten)}
                                className={`w-6 h-6 border flex items-center justify-center transition-all focus:outline-none ${
                                  isEaten ? "bg-white text-black border-white" : "border-neutral-800 hover:border-neutral-500"
                                }`}
                                data-testid={`meal-checkbox-${idx}`}
                              >
                                {isEaten && <Check className="w-4 h-4" />}
                              </button>

                              <div>
                                <span className="text-[9px] uppercase font-mono tracking-widest text-neutral-500 block">{meal.meal_type}</span>
                                <h4 className={`text-sm uppercase font-bold ${isEaten ? "line-through text-neutral-500" : "text-white"}`}>{meal.name}</h4>
                              </div>
                            </div>

                            <div className="text-right font-mono text-[11px] text-neutral-400">
                              <span>{meal.calories} kcal • P: {meal.protein}g • C: {meal.carbs}g • F: {meal.fats}g</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                /* No Meal plan - Request Form layout */
                <div className="max-w-xl mx-auto border border-neutral-800 p-8 bg-neutral-950/20" data-testid="request-meal-plan-container">
                  <h3 className="font-bebas text-2xl uppercase tracking-wider mb-6">Request Customized Meal Plan</h3>
                  
                  {hasPendingMealReq ? (
                    <div className="text-center py-8 space-y-4" data-testid="pending-request-alert">
                      <Clock className="w-8 h-8 text-neutral-600 mx-auto animate-pulse" />
                      <div className="space-y-1">
                        <span className="text-[10px] uppercase font-bold bg-neutral-900 border border-neutral-800 text-neutral-400 px-3 py-1 font-mono">
                          STATUS: Pending Review
                        </span>
                        <h4 className="font-bebas text-lg tracking-wide uppercase pt-3">In Review with Instructor Stokes</h4>
                        <p className="text-xs text-neutral-400 font-manrope">
                          Your custom goals requested on {pendingMealDetails ? new Date(pendingMealDetails.created_at).toLocaleDateString() : "recently"} are being analyzed. Stokes will compile your macro layout shortly!
                        </p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleRequestMealPlan} className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Current Weight (lbs)</label>
                          <input
                            type="number"
                            required
                            placeholder="e.g. 185"
                            value={currentWeight}
                            onChange={(e) => setCurrentWeight(e.target.value)}
                            className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none"
                            data-testid="meal-req-current-weight"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Target Weight (lbs)</label>
                          <input
                            type="number"
                            required
                            placeholder="e.g. 175"
                            value={targetWeight}
                            onChange={(e) => setTargetWeight(e.target.value)}
                            className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none"
                            data-testid="meal-req-target-weight"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Primary Fitness Goals</label>
                        <textarea
                          required
                          placeholder="e.g. Body fat percentage reduction, lean tissue gain, or specific endurance challenges..."
                          value={fitnessGoals}
                          onChange={(e) => setFitnessGoals(e.target.value)}
                          rows={3}
                          className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none resize-none"
                          data-testid="meal-req-goals"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Dietary Restrictions & Allergies</label>
                        <input
                          type="text"
                          placeholder="e.g. Dairy free, gluten free, vegetarian, or none"
                          value={dietaryRestrictions}
                          onChange={(e) => setDietaryRestrictions(e.target.value)}
                          className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none"
                          data-testid="meal-req-allergies"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Additional Notes</label>
                        <input
                          type="text"
                          placeholder="Any preferences or specific macronutrient ratios you prefer"
                          value={mealReqNotes}
                          onChange={(e) => setMealReqNotes(e.target.value)}
                          className="w-full bg-transparent border-b border-neutral-800 py-2 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none"
                          data-testid="meal-req-notes"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all duration-300"
                        data-testid="meal-req-submit-btn"
                      >
                        {loading ? "Submitting..." : "Submit Customized Layout Request"}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: MY COACHING SESSIONS */}
          {activeTab === "my-coaching" && (
            <div className="space-y-8" data-testid="user-bookings-view">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Column: List bookings */}
                <div className="lg:col-span-5 space-y-6">
                  <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block pb-2 border-b border-neutral-900">
                    My Scheduled Sessions ({myBookings.length})
                  </span>

                  {myBookings.length === 0 ? (
                    <div className="border border-dashed border-neutral-800 py-12 text-center text-xs text-neutral-500 uppercase tracking-widest">
                      No sessions booked yet. Use the scheduler on the right to book your first slots!
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {myBookings.map((b) => (
                        <div key={b.id} className="border border-neutral-800 p-5 bg-neutral-950/20" data-testid={`my-booking-item-${b.id}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="font-bebas text-lg tracking-wide uppercase">1-on-1 Virtual Session</h5>
                              <span className="text-[10px] text-neutral-500 uppercase tracking-widest">Date: {b.preferred_date}</span>
                            </div>
                            <span className={`text-[9px] uppercase font-bold px-2 py-0.5 ${
                              b.status === "approved" ? "bg-white text-black" : b.status === "declined" ? "bg-neutral-800 text-neutral-500 font-bold" : "border border-neutral-700 text-neutral-400"
                            }`}>
                              {b.status}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-300 font-mono mt-2">Preferred Slot: {b.preferred_time}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Column: Schedule Form with custom upcoming days calendar grid */}
                <div className="lg:col-span-7 border border-neutral-800 p-8 bg-neutral-950/20">
                  <h3 className="font-bebas text-2xl uppercase tracking-wider mb-6">Virtual Session Scheduler</h3>

                  <form onSubmit={handleBookSession} className="space-y-6">
                    {/* Day list picker grid */}
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                        Select Preferred Date (Upcoming 7 Days)
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="booking-dates-selector">
                        {dates_to_add.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setSelectedDate(item.dateString)}
                            className={`p-3 border text-left transition-all rounded-none ${
                              selectedDate === item.dateString
                                ? "border-white bg-white text-black font-extrabold"
                                : "border-neutral-800 hover:border-neutral-500 bg-black/40 text-neutral-400 hover:text-white"
                            }`}
                            data-testid={`date-option-${item.dateString}`}
                          >
                            <span className="text-[10px] font-mono block leading-none mb-1">
                              {item.id}
                            </span>
                            <span className="text-xs uppercase font-bold block truncate leading-none">
                              {item.label.split(",")[0]}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Time slots horizontal selector */}
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                        Select Available Time Slot
                      </label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-time-selector">
                        {slots.map((s, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setSelectedTime(s)}
                            className={`p-2 border text-center text-xs font-mono transition-all rounded-none ${
                              selectedTime === s
                                ? "border-white bg-white text-black font-extrabold"
                                : "border-neutral-800 hover:border-neutral-500 text-neutral-400 hover:text-white"
                            }`}
                            data-testid={`time-option-${s}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Client request notes */}
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">
                        Meeting notes / Focus goals (Optional)
                      </label>
                      <textarea
                        rows={2}
                        placeholder="e.g. Discussing deadlift mechanics form, or designing a specific macro ratio layout..."
                        value={bookingNotes}
                        onChange={(e) => setBookingNotes(e.target.value)}
                        className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none resize-none"
                        data-testid="booking-notes-input"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all duration-300"
                      data-testid="booking-submit-btn"
                    >
                      {loading ? "Scheduling..." : "Send Virtual Booking Inquiry to Stokes"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: SPIRITUAL GROWTH JOURNAL */}
          {activeTab === "spiritual-journal" && (
            <div className="space-y-8" data-testid="user-journal-view">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Column: Daily Devotional WOD and submission form */}
                <div className="lg:col-span-6 space-y-6">
                  {todayDevotional && (
                    <div className="border border-neutral-800 p-8 bg-neutral-950/20 relative" data-testid="todays-devotional-panel">
                      <div className="absolute top-0 right-0 border-b border-l border-neutral-800 px-3 py-1 font-bebas text-[9px] text-neutral-500">
                        DAILY REFLECTION WOD
                      </div>
                      
                      <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block mb-1">
                        SCRIPTURE OF THE DAY: {todayDevotional.scripture}
                      </span>
                      <blockquote className="italic text-base text-neutral-200 border-l-2 border-white pl-4 my-4 font-light font-manrope">
                        " {todayDevotional.verse} "
                      </blockquote>
                      
                      <h4 className="font-bebas text-2xl uppercase tracking-wider mt-6 mb-2">
                        {todayDevotional.title}
                      </h4>
                      <p className="text-xs text-neutral-300 leading-relaxed font-manrope font-light mb-6">
                        {todayDevotional.message}
                      </p>

                      <div className="border-t border-neutral-900 pt-4">
                        <span className="text-[10px] uppercase font-bold text-neutral-400 tracking-widest block mb-1 font-mono">
                          PHYSICAL / SPIRITUAL EXERCISE INSTRUCTION
                        </span>
                        <p className="text-xs font-mono text-neutral-300 font-bold bg-neutral-900 border border-neutral-800 p-3 leading-relaxed">
                          {todayDevotional.wod}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Submission form */}
                  <div className="border border-neutral-800 p-8 bg-neutral-950/20">
                    <h4 className="font-bebas text-xl uppercase tracking-wider mb-4">Log Today's Reflection</h4>
                    <form onSubmit={handleLogDailyDevotional} className="space-y-4">
                      <textarea
                        required
                        rows={3}
                        placeholder="Write down your thoughts, reflections, prayers, or physical logs for today..."
                        value={dailyReflectionInput}
                        onChange={(e) => setDailyReflectionInput(e.target.value)}
                        className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none resize-none"
                        data-testid="journal-note-input"
                      />
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-white text-black py-2.5 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all duration-300"
                        data-testid="journal-submit-btn"
                      >
                        {loading ? "Recording..." : "Save Log to Spiritual Journal"}
                      </button>
                    </form>
                  </div>
                </div>

                {/* Right Column: Historical Journal reflections list */}
                <div className="lg:col-span-6 space-y-6">
                  <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block pb-2 border-b border-neutral-900">
                    My Spiritual Growth History ({reflections.length} Entries)
                  </span>

                  {reflections.length === 0 ? (
                    <div className="border border-dashed border-neutral-800 py-12 text-center text-xs text-neutral-500 uppercase tracking-widest">
                      Your journal is currently empty. Make your first devotional entry on the left!
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2" data-testid="journal-logs-list">
                      {reflections.map((ref) => (
                        <div key={ref.id} className="border border-neutral-800 p-5 bg-neutral-950/20 hover:border-neutral-700 transition-all">
                          <span className="text-[10px] font-mono text-neutral-500 block mb-1">
                            LOGGED ON: {ref.date}
                          </span>
                          <p className="text-xs text-neutral-300 leading-relaxed font-manrope font-light whitespace-pre-line">
                            {ref.reflections}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
